/**
 * Geometry for a probability tree drawn left to right: a start dot, one column of
 * labelled boxes per stage, probabilities written on the branches, and the outcome
 * column at the right with each path's probability.
 *
 * Pure positions in viewBox units, so the preview and the exported SVG and PNG are the
 * same picture. Leaves take evenly spaced rows; every parent sits at the middle of its
 * children, so no two boxes overlap by construction.
 */

import {
  CHART_PADDING,
  layoutTitle,
  type TitleBlock,
} from "@/lib/tool-kit/charts/layout";
import {
  estimateTextWidth,
  maxTextWidth,
  type TextStyle,
  truncateText,
} from "@/lib/tool-kit/charts/text";
import type { TreeLeaf, TreeNode } from "./probability-tree";
import type { Rational } from "./rational";

export const NODE_FONT: TextStyle = { fontSize: 13 };
export const BRANCH_FONT: TextStyle = { fontSize: 11 };
export const OUTCOME_FONT: TextStyle = { fontSize: 12 };
export const BOX_HEIGHT = 24;
export const ROW_HEIGHT = 34;
const BOX_PAD = 8;
const MAX_LABEL_WIDTH = 150;
const MIN_RUN = 72;
const MIN_WIDTH = 480;
const LEADER = 26;
const ROOT_RADIUS = 5;

export interface PlacedNode {
  id: string;
  label: string;
  /** Left edge and vertical centre of the box. */
  x: number;
  y: number;
  width: number;
  /** On the path to a selected outcome. */
  onSelectedPath: boolean;
}

export interface PlacedEdge {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  label: string;
  labelWidth: number;
  onSelectedPath: boolean;
}

export interface PlacedOutcome {
  id: string;
  /** Where the dotted leader starts (the leaf's right edge) and the text begins. */
  fromX: number;
  textX: number;
  y: number;
  text: string;
  selected: boolean;
}

export interface TreeLayout {
  width: number;
  height: number;
  title: TitleBlock | null;
  root: { x: number; y: number; r: number };
  nodes: PlacedNode[];
  edges: PlacedEdge[];
  outcomes: PlacedOutcome[];
}

interface Walked {
  node: TreeNode;
  id: string;
  depth: number;
  children: Walked[];
  y: number;
}

function walk(
  nodes: readonly TreeNode[],
  prefix: string,
  depth: number,
  counter: { next: number }
): Walked[] {
  return nodes.map((node, index) => {
    const id = prefix === "" ? String(index) : `${prefix}/${index}`;
    const children = walk(node.children, id, depth + 1, counter);
    let y: number;
    if (children.length === 0) {
      y = counter.next;
      counter.next += 1;
    } else {
      y = ((children[0]?.y ?? 0) + (children.at(-1)?.y ?? 0)) / 2;
    }
    return { node, id, depth, children, y };
  });
}

function flatten(items: readonly Walked[]): Walked[] {
  return items.flatMap((item) => [item, ...flatten(item.children)]);
}

/** Ids of every node on the way to a selected outcome, the outcome's own included. */
function selectedPathIds(selected: ReadonlySet<string>): Set<string> {
  const ids = new Set<string>();
  for (const leafId of selected) {
    const parts = leafId.split("/");
    for (let end = 1; end <= parts.length; end++) {
      ids.add(parts.slice(0, end).join("/"));
    }
  }
  return ids;
}

export function layoutTree(input: {
  roots: readonly TreeNode[];
  leaves: readonly TreeLeaf[];
  title: string;
  /** Text for a branch probability. */
  format: (probability: Rational) => string;
  /** Text for an outcome row: its path and probability. */
  outcomeText: (leaf: TreeLeaf) => string;
  selected: ReadonlySet<string>;
}): TreeLayout {
  const counter = { next: 0 };
  const tree = walk(input.roots, "", 1, counter);
  const all = flatten(tree);
  const depthCount = Math.max(1, ...all.map((item) => item.depth));

  // Column widths: the widest label and branch label at each depth.
  const boxWidth: number[] = [];
  const runLength: number[] = [];
  for (let depth = 1; depth <= depthCount; depth++) {
    const atDepth = all.filter((item) => item.depth === depth);
    const labels = atDepth.map((item) =>
      truncateText(item.node.label, MAX_LABEL_WIDTH, NODE_FONT)
    );
    boxWidth[depth] = maxTextWidth(labels, NODE_FONT) + 2 * BOX_PAD;
    const branchWidth = maxTextWidth(
      atDepth.map((item) => input.format(item.node.prob)),
      BRANCH_FONT
    );
    runLength[depth] = Math.max(MIN_RUN, branchWidth + 30);
  }

  const columnX: number[] = [];
  let cursor = CHART_PADDING + ROOT_RADIUS * 2;
  const rootRight = cursor;
  for (let depth = 1; depth <= depthCount; depth++) {
    cursor += runLength[depth];
    columnX[depth] = cursor;
    cursor += boxWidth[depth];
  }
  const outcomeX = cursor + LEADER;
  const outcomeWidth = maxTextWidth(
    input.leaves.map((leaf) => input.outcomeText(leaf)),
    { ...OUTCOME_FONT, bold: true }
  );
  const width = Math.max(
    MIN_WIDTH,
    Math.ceil(outcomeX + outcomeWidth + CHART_PADDING)
  );

  const title = layoutTitle(input.title, width);
  const top = CHART_PADDING + (title?.height ?? 0);
  const rowY = (row: number) => top + ROW_HEIGHT / 2 + row * ROW_HEIGHT;
  const height = Math.ceil(top + counter.next * ROW_HEIGHT + CHART_PADDING);
  const selectedIds = selectedPathIds(input.selected);

  const byId = new Map(all.map((item) => [item.id, item]));
  const parentOf = (item: Walked): Walked | undefined =>
    byId.get(item.id.split("/").slice(0, -1).join("/"));
  const rootY = ((tree[0]?.y ?? 0) + (tree.at(-1)?.y ?? 0)) / 2;

  const nodes: PlacedNode[] = all.map((item) => ({
    id: item.id,
    label: truncateText(item.node.label, MAX_LABEL_WIDTH, NODE_FONT),
    x: columnX[item.depth],
    y: rowY(item.y),
    width: boxWidth[item.depth],
    onSelectedPath: selectedIds.has(item.id),
  }));

  const edges: PlacedEdge[] = all.map((item) => {
    const parent = parentOf(item);
    const label = input.format(item.node.prob);
    return {
      id: item.id,
      x1: parent ? columnX[parent.depth] + boxWidth[parent.depth] : rootRight,
      y1: rowY(parent ? parent.y : rootY),
      x2: columnX[item.depth],
      y2: rowY(item.y),
      label,
      labelWidth: estimateTextWidth(label, BRANCH_FONT),
      onSelectedPath: selectedIds.has(item.id),
    };
  });

  const outcomes: PlacedOutcome[] = input.leaves.map((leaf) => {
    const item = byId.get(leaf.id) as Walked;
    return {
      id: leaf.id,
      fromX: columnX[item.depth] + boxWidth[item.depth],
      textX: outcomeX,
      y: rowY(item.y),
      text: input.outcomeText(leaf),
      selected: input.selected.has(leaf.id),
    };
  });

  return {
    width,
    height,
    title,
    root: {
      x: CHART_PADDING + ROOT_RADIUS,
      y: rowY(rootY),
      r: ROOT_RADIUS,
    },
    nodes,
    edges,
    outcomes,
  };
}
